import VideoCallIcon from "@mui/icons-material/VideoCall";
import { Avatar, Box, Chip, Grid } from "@mui/material";
import React from "react";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import {
  TIME_ZONE_MAPPER,
  formatDate,
  formatDurationFromMinutes,
  formatTime,
} from "../../../../../utils/date-time-utility";
import {
  APPOINTMENT_MODE_COLORS,
  APPOINTMENT_STATUS_MAPPER,
  APPOINTMENT_STATUS_STYLE,
  BUTTON_STYLES,
  CARD_STYLES,
  COLORS,
} from "../constants";
import {
  Appointment,
  AppointmentActions,
} from "../patient-portal-dashboard.types";

interface AppointmentCardProps {
  appointment: Appointment;
  actions?: AppointmentActions;
}

const DashboardAppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  actions,
}) => {
  const handleCancel = () => actions?.onCancel?.(appointment);
  const handleReschedule = () => actions?.onReschedule?.(appointment.uuid);

  const joinInstantAppointment = (appointmentUrl: string) => {
    return `/app/preview?room=${appointmentUrl?.split("room=")?.[1]}`;
  };

  const handleJoinVisit = () =>
    actions?.onJoinVisit?.(joinInstantAppointment(appointment.patientJoinLink));
  const [patient] = useCurrentUser();
  const modeConfig = APPOINTMENT_MODE_COLORS[appointment.mode];
  // Join Visit shows only for joinable (telehealth) appointments — i.e. those
  // with a patientJoinLink — that are confirmed. In-person visits have a null
  // join link, so the button is correctly hidden. The join link is used as the
  // mode signal because it's reliable across screens, unlike the mode string.
  const isJoinVisitEnabled =
    !!appointment.patientJoinLink &&
    (appointment.appStatusForPatient === "CONFIRMED" ||
      appointment.status === "CONFIRMED" ||
      (appointment.status as string) === "CHECKED_IN");

  return (
    <Box
      sx={{
        ...CARD_STYLES,
        width: "100%",
        "&:hover": CARD_STYLES.hover,
      }}
    >
      {/* Doctor Info Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          p: 1,
          borderBottom: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.doctorInfoBg,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{ width: 48, height: 48 }}
            src={appointment.providerProfilePicture}
          />
          <Box>
            <Text
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                color: COLORS.providerName,
              }}
            >
              {appointment.providerName}
            </Text>
            <Text
              sx={{
                fontSize: "12px",
                color: COLORS.providerRole,
                fontWeight: "400",
              }}
            >
              {appointment.providerRole}
            </Text>
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: modeConfig?.bgColor,
            color: modeConfig?.color,
            border: `1px solid ${modeConfig?.color}`,
            px: 1.5,
            py: 0.5,
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          <VideoCallIcon sx={{ fontSize: "16px" }} />
          {modeConfig?.label}
        </Box>
      </Box>

      {/* Details + Actions Section */}
      <Box
        sx={{
          backgroundColor: COLORS.white,
          borderRadius: "8px",
          p: 1.5,
          pl: 3,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${COLORS.border}` }}
        >
          <Grid item xs={6}>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.detailLabel,
                mb: 0.5,
              }}
            >
              Date
            </Text>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.textPrimary,
              }}
            >
              {appointment?.appointmentTime
                ? formatDate(
                    appointment.appointmentTime
                    // patient?.timezone as TimezoneEnum
                  )
                : "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.detailLabel,
                mb: 0.5,
              }}
            >
              Time
            </Text>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.textPrimary,
              }}
            >
              {appointment?.appointmentTime
                ? `${formatTime(appointment.appointmentTime, {
                    timezone: patient?.timezone
                      ? (TIME_ZONE_MAPPER as any)?.[patient.timezone]
                      : undefined,
                  })}${
                    appointment?.duration
                      ? ` (${formatDurationFromMinutes(Number(appointment.duration))})`
                      : ""
                  }`
                : "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.detailLabel,
                mb: 0.5,
              }}
            >
              Reason for Visit
            </Text>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
              title={appointment?.chiefComplaint || "-"}
            >
              {appointment?.chiefComplaint || "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: COLORS.detailLabel,
                mb: 0.5,
              }}
            >
              Status
            </Text>
            {appointment.status ? (
              <Chip
                label={APPOINTMENT_STATUS_MAPPER[appointment.status]}
                size="small"
                sx={{
                  backgroundColor:
                    APPOINTMENT_STATUS_STYLE[appointment.status]?.bgColor,
                  color: APPOINTMENT_STATUS_STYLE[appointment.status]?.color,
                  fontSize: "12px",
                  fontWeight: 500,
                  height: "18px",
                  width: "77px",
                  borderRadius: "12px",
                }}
              />
            ) : (
              <Text sx={{ fontSize: "14px", color: COLORS.textPrimary }}>
                -
              </Text>
            )}
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Flex gap={1}>
            <StyledButtonV2
              variant="outlined"
              size="small"
              label="Cancel"
              sx={{
                backgroundColor: COLORS.cancelBg,
                color: COLORS.cancel,
                borderColor: COLORS.cancel,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                minWidth: "90px",
                height: "32px",
                "&:hover": {
                  backgroundColor: COLORS.cancelHover,
                  borderColor: COLORS.cancel,
                },
              }}
              onClick={handleCancel}
            />
            <StyledButtonV2
              variant="outlined"
              size="small"
              label="Reschedule"
              sx={{
                backgroundColor: COLORS.rescheduleBg,
                color: COLORS.reschedule,
                borderColor: COLORS.reschedule,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                minWidth: "130px",
                height: "32px",
                "&:hover": {
                  backgroundColor: COLORS.rescheduleHover,
                  borderColor: COLORS.reschedule,
                },
              }}
              onClick={handleReschedule}
            />
          </Flex>
          {isJoinVisitEnabled && (
            <StyledButtonV2
              variant="contained"
              size="small"
              label="Join Visit"
              sx={{
                ...BUTTON_STYLES.contained,
                backgroundColor: COLORS.joinVisit,
                "&:hover": { backgroundColor: COLORS.joinVisitHover },
                fontWeight: 500,
                minWidth: "90px",
                height: "32px",
              }}
              onClick={handleJoinVisit}
            />
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default DashboardAppointmentCard;
